import { useEffect, useRef, useState } from 'react';
import type { AppSearchResult } from './appSearchTypes';

export interface UseAppSearchKeyboardNavArgs {
  /** Only listen while the dropdown is open. */
  open: boolean;
  results: AppSearchResult[];
  onSelect: (result: AppSearchResult) => void;
  onClose: () => void;
}

export interface UseAppSearchKeyboardNavValue {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
}

/**
 * #1392 — document-level keyboard navigation for the app-search dropdown.
 *
 * After Cmd/Ctrl+F the focus deliberately stays in the header search
 * input (in the design-system Header), so a dropdown-local ``onKeyDown``
 * never sees Arrow/Enter/Escape. This hook listens on ``document`` while
 * the dropdown is open and drives the highlighted row + selection from
 * wherever focus is. ``Enter`` is only consumed when there are results,
 * so a normal submit-less search box keeps behaving when the list is
 * empty.
 */
export function useAppSearchKeyboardNav({
  open,
  results,
  onSelect,
  onClose,
}: UseAppSearchKeyboardNavArgs): UseAppSearchKeyboardNavValue {
  const [activeIndex, setActiveIndex] = useState(-1);

  // Reset the highlight whenever the result set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  // Keep the latest values in refs so the document listener stays stable.
  const stateRef = useRef({ results, activeIndex, onSelect, onClose });
  stateRef.current = { results, activeIndex, onSelect, onClose };

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      const { results: rs, activeIndex: idx, onSelect: sel, onClose: close } =
        stateRef.current;

      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (rs.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % rs.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? rs.length - 1 : i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const chosen = rs[idx >= 0 ? idx : 0];
        if (chosen) sel(chosen);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return { activeIndex, setActiveIndex };
}

export default useAppSearchKeyboardNav;
